import { useCallback, type ChangeEvent } from "react";
import { RANGES, type NumericField, type Units } from "../lib/config";
import type { Glider } from "../lib/glider";
import { wingLoading_kg_m2 } from "../lib/mass";
import {
  SLIDER_LABELS,
  formatBallastValue,
  formatSliderValue,
} from "../lib/slider-format";
import { useConfiguration } from "../hooks/useConfiguration";

type SliderProps = {
  readonly field: NumericField;
  readonly value: number;
  readonly units: Units;
  /** Overrides `RANGES[field].max` — used for the per-glider water-ballast cap. */
  readonly max?: number;
  /** Overrides the formatted readout — used for the combined ballast readout. */
  readonly valueText?: string;
};

function Slider({ field, value, units, max, valueText }: SliderProps) {
  const { dispatch } = useConfiguration();
  const { min, max: rangeMax, step } = RANGES[field];
  const sliderMax = max ?? rangeMax;
  const label = SLIDER_LABELS[field];
  const text = valueText ?? formatSliderValue(field, value, units);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      dispatch({
        type: "setField",
        field,
        value: Number.parseFloat(e.target.value),
      }),
    [dispatch, field],
  );

  return (
    <div className="mb-3">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs text-ink">{label}</span>
        <span className="text-[13px] font-semibold tabular-nums text-accent">
          {text}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={sliderMax}
        step={step}
        value={value}
        onChange={onChange}
        aria-label={label}
        aria-valuetext={text}
        className="h-11 w-full cursor-pointer accent-accent focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 md:h-6"
      />
    </div>
  );
}

/** Pilot / water / bugs / MacCready / wind sliders. */
export function ConfigSliders({ glider }: { readonly glider: Glider }) {
  const { config } = useConfiguration();
  const { pilotWeight_kg, waterBallast_kg, units } = config;

  // A shared link may carry more water than this glider can hold; cap at the
  // placarded maximum everywhere it is read so the slider and polar agree.
  const water = Math.min(waterBallast_kg, glider.maxBallast_kg);
  const hasBallast = glider.maxBallast_kg > 0;
  const wingLoading = wingLoading_kg_m2(glider, pilotWeight_kg, water);

  return (
    <>
      <fieldset className="mb-5">
        <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
          Configuration
        </legend>
        <Slider field="pilotWeight_kg" value={pilotWeight_kg} units={units} />
        {hasBallast ? (
          <Slider
            field="waterBallast_kg"
            value={water}
            units={units}
            max={glider.maxBallast_kg}
            valueText={formatBallastValue(water, wingLoading)}
          />
        ) : (
          <p className="mb-3 text-xs text-muted">
            No water ballast ·{" "}
            <span className="font-semibold tabular-nums text-accent">
              {wingLoading.toFixed(1)} kg/m²
            </span>
          </p>
        )}
        <Slider field="bugsPercent" value={config.bugsPercent} units={units} />
      </fieldset>
      <fieldset>
        <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
          Speed-to-fly
        </legend>
        <Slider
          field="macCready_ms"
          value={config.macCready_ms}
          units={units}
        />
        <Slider
          field="windHeadwind_kmh"
          value={config.windHeadwind_kmh}
          units={units}
        />
      </fieldset>
    </>
  );
}
